/**
 * Раскрытый код возражения (#846): формулировки, цитаты, лучшие отработки,
 * этапы и движение сделки, операторы. В разделе «Голос клиента» взгляд рынка
 * начинается с ряда кода (#2394 §9 п. 7, #2556): «Период» | «12 месяцев», и
 * столбец периода сужает цитаты до разговоров своей корзины.
 *
 * Этап и движение сделки есть не у каждой организации (этап пишет шаг под
 * флагом `stageAwareScoring`), поэтому раздел без единого известного значения
 * не показывается вовсе, а не заполняется «без этапа 100 %».
 *
 * Монтируется раскрытием, как раскрытие строки (`VoiceRowDetails`): свёрнутый
 * и снова раскрытый код начинает с целого периода, а не со старой корзины.
 */
import { useTranslation } from 'react-i18next';
import type { ObjectionStreamCode } from '@aica/api-types/schemas';
import { BestAnswerExcerpt } from './BestAnswerExcerpt';
import { ObjectionQuotes } from './ObjectionQuotes';
import { percent, stageRows, type ObjectionsViewMode } from './objection-view-model';
import { VoiceBucketHeader } from '../voice/VoiceBucketHeader';
import { VoiceRowTrend } from '../voice/VoiceRowTrend';
import {
  bucketNote,
  pickedItems,
  useTrendBucket,
  type BucketListKeys,
} from '../voice/useTrendBucket';
import type { VoiceRowDetailsTrend } from '../voice/voice-series-view-model';

const PROGRESSION_ORDER = ['advanced', 'held', 'regressed', 'unknown'] as const;

/**
 * The code's quotes are not its latest conversations: one per call, the
 * freshest first, a new employee each before anyone's second (`pickQuotes`).
 */
const QUOTES_LIST: BucketListKeys = {
  notListed: 'objectionsPage.bucketNotListed',
  shown: 'objectionsPage.bucketShown',
};

interface ObjectionCodeDetailsProps {
  id: string;
  code: ObjectionStreamCode;
  mode: ObjectionsViewMode;
  /** The code's series — the market view's in the Voice section only. */
  series?: VoiceRowDetailsTrend;
  onOpenCall: (recordId: string, quote: string) => void;
}

export function ObjectionCodeDetails({
  id,
  code,
  mode,
  series,
  onOpenCall,
}: ObjectionCodeDetailsProps) {
  const { t } = useTranslation(['dashboard', 'records']);
  const handledShare = percent(code.calls - code.unhandledCalls, code.calls);
  const stages = stageRows(code.byStage);
  const hasStage = stages.some(([stage]) => stage !== 'unknown' && stage !== 'unmapped');
  const hasProgression = PROGRESSION_ORDER.some(
    (key) => key !== 'unknown' && code.byProgression[key] > 0
  );
  const pick = useTrendBucket(series);
  const quotes = pickedItems(pick, code.quotes);
  const quotesNote = bucketNote(pick, quotes.length, t, QUOTES_LIST);

  return (
    <div id={id} className="space-y-4 border-t border-border p-3">
      {series && (
        <VoiceRowTrend
          seriesRef={series.seriesRef}
          calls={code.calls}
          series={series.series}
          unit={series.unit}
          selected={pick.index}
          onSelect={pick.select}
          monthPatch={series.monthPatch}
        />
      )}
      {code.wordings.length > 0 && (
        <p className="text-xs text-fg-subtle">
          {t('objectionsPage.wordings', { wordings: code.wordings.join(' · ') })}
        </p>
      )}

      {mode === 'team' && (
        <section>
          <h4 className="mb-1 text-xs font-medium text-fg-muted">
            {t('objectionsPage.team.handledShare', { share: handledShare })}
          </h4>
          {code.weakestAgents.length > 0 ? (
            <ul className="space-y-1 text-xs">
              {code.weakestAgents.map((agent) => (
                <li key={agent.agentId} className="flex justify-between">
                  <span className="text-fg">
                    {agent.agentName ?? t('objectionsPage.best.unknownAgent')}
                  </span>
                  <span className="font-mono text-danger">
                    {t('objectionsPage.team.unhandled', {
                      unhandled: agent.unhandledCalls,
                      count: agent.calls,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-fg-muted">
              {t('objectionsPage.team.noWeakAgents')}
            </p>
          )}
        </section>
      )}

      {pick.bucket && code.quotes.length > 0 ? (
        // A bucket of the period: the quotes of its conversations, out of
        // the few the code carries — how many of its K, said below. No
        // quote at all (none with words, or the grant withholds them):
        // nothing to narrow, and no line blaming the list for it.
        <section className="space-y-2">
          <VoiceBucketHeader period={pick.text} onClear={pick.clear} />
          {quotes.length > 0 && (
            <ObjectionQuotes quotes={quotes} onOpenCall={onOpenCall} />
          )}
          {quotesNote && <p className="text-xs text-fg-subtle">{quotesNote}</p>}
        </section>
      ) : (
        code.quotes.length > 0 && (
          <section>
            <h4 className="mb-1 text-xs font-medium text-fg-muted">
              {t('objectionsPage.quotes')}
            </h4>
            <ObjectionQuotes quotes={code.quotes} onOpenCall={onOpenCall} />
          </section>
        )
      )}

      <section>
        <h4 className="mb-1 text-xs font-medium text-fg-muted">
          {t('objectionsPage.best.title')}
        </h4>
        {code.bestHandled.length > 0 ? (
          <div className="grid gap-2 md:grid-cols-3">
            {code.bestHandled.map((answer) => (
              <BestAnswerExcerpt
                key={answer.recordId}
                answer={answer}
                onOpen={onOpenCall}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-fg-muted">{t('objectionsPage.best.none')}</p>
        )}
      </section>

      {(hasStage || hasProgression) && (
        <div className="grid gap-4 md:grid-cols-2">
          {hasStage && (
            <section>
              <h4 className="mb-1 text-xs font-medium text-fg-muted">
                {t('objectionsPage.stages.title')}
              </h4>
              <ul className="space-y-1 text-xs">
                {stages.map(([stage, calls]) => (
                  <li key={stage} className="flex items-center gap-2">
                    <span className="w-28 shrink-0 truncate text-fg">
                      {stage === 'unknown' || stage === 'unmapped'
                        ? t(`objectionsPage.stages.${stage}`)
                        : t(`records:records.stageSemantic.${stage}`, stage)}
                    </span>
                    {(code.byStageGuessed[stage] ?? 0) > 0 && (
                      <span
                        className="shrink-0 text-fg-subtle"
                        title={t('objectionsPage.stages.guessedHint')}
                      >
                        {t('objectionsPage.stages.guessed', {
                          count: code.byStageGuessed[stage],
                        })}
                      </span>
                    )}
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-elevated">
                      <span
                        className="block h-1.5 rounded-full bg-primary"
                        style={{ width: `${percent(calls, code.calls)}%` }}
                      />
                    </span>
                    <span className="w-8 text-right font-mono text-fg-muted">
                      {calls}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {hasProgression && (
            <section>
              <h4 className="mb-1 text-xs font-medium text-fg-muted">
                {t('objectionsPage.progression.title')}
              </h4>
              <ul className="space-y-1 text-xs">
                {PROGRESSION_ORDER.filter((key) => code.byProgression[key] > 0).map(
                  (key) => (
                    <li key={key} className="flex justify-between">
                      <span className="text-fg">
                        {key === 'unknown'
                          ? t('objectionsPage.progression.unknown')
                          : t(`records:records.analysisPanel.stageProgression.${key}`)}
                      </span>
                      <span className="font-mono text-fg-muted">
                        {code.byProgression[key]} ·{' '}
                        {percent(code.byProgression[key], code.calls)}%
                      </span>
                    </li>
                  )
                )}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
